import React from "react";
import SkillsGrid from "./skills";
import { MdOutlineWork, MdOutlineSchool } from "react-icons/md";
import { IoDocumentTextOutline } from "react-icons/io5";

export default function Resume() {
  return (
    <article className="animate-fade-in" data-page="resume">
      <header className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold font-display text-white">Resume</h2>
          <div className="w-12 h-1 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full mt-2"></div>
        </div>
        <a
          href="https://docs.google.com/document/d/1FuhTAMiHH_nTtiA28yzb6SbtU5xev_jkRabrtUWtvaU/edit?tab=t.0"
          className="hidden sm:flex items-center gap-2 text-xs font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 px-4 py-2.5 rounded-xl shadow-lg shadow-indigo-500/20 transition-all hover:-translate-y-0.5"
          target="_blank"
          rel="noreferrer"
        >
          <IoDocumentTextOutline className="text-sm" />
          Download CV
        </a>
      </header>

      {/* Experience Timeline */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-violet-400 shadow-md">
            <MdOutlineWork className="text-lg" />
          </div>
          <h3 className="text-xl font-semibold font-display text-white">Experience</h3>
        </div>

        <div className="relative pl-8 border-l border-zinc-800/80 ml-5 space-y-8">
          {/* Nspark Software LLC */}
          <div className="relative">
            <span className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-zinc-950 border-2 border-violet-500 shadow-sm shadow-violet-500/50"></span>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-base font-bold text-white font-display">
                AI Full-Stack Developer
              </h4>
              <span className="text-[10px] font-semibold text-violet-400 bg-violet-500/10 border border-violet-500/20 px-2.5 py-1 rounded-full">
                Jan 2026 – May 2026
              </span>
            </div>

            <span className="text-xs font-medium text-zinc-400 block mt-1">
              Nspark Software LLC
            </span>

            <ul className="list-disc list-outside ml-4 mt-3 text-xs text-zinc-400 space-y-1.5 font-sans leading-relaxed">
              <li>Led end-to-end development from Figma to AWS EC2 deployment, building a reusable React.js component library to accelerate frontend delivery.</li>
              <li>Architected scalable Node.js/Express APIs with PostgreSQL and MongoDB, reducing response times by ~30% via strategic database indexing.</li>
              <li>Integrated OpenAI APIs via Langchain to deploy an automated RAG pipeline for document retrieval, an in-app Helper Bot, and AI-driven data summarization.</li>
              <li>Engineered a dynamic data pipeline that processes file uploads and user metadata to automatically generate database schemas.</li>
              <li>Collaborated directly with stakeholders in rapid feedback cycles to prioritize requirements and continuously ship features to production.</li>
            </ul>
          </div>

          {/* WhatBytes */}
          <div className="relative">
            <span className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-zinc-950 border-2 border-indigo-500 shadow-sm shadow-indigo-500/50"></span>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-base font-bold text-white font-display">
                Full-Stack Engineer (SDE)
              </h4>
              <span className="text-[10px] font-semibold text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-1 rounded-full">
                May 2025 – Dec 2025
              </span>
            </div>

            <span className="text-xs font-medium text-zinc-400 block mt-1">
              WhatBytes
            </span>

            <ul className="list-disc list-outside ml-4 mt-3 text-xs text-zinc-400 space-y-1.5 font-sans leading-relaxed">
              <li>Led frontend engineering for two major platforms (Alphagen and OptimizingAI). Designed and built a complex drag-and-drop filtering tool in Alphagen, enabling users to apply nested AND/OR conditions across dynamic attributes.</li>
              <li>Improved overall system responsiveness by ~30% through backend database indexing, query optimization, and efficient client-side state management utilizing Zustand and TanStack Query.</li>
              <li>Engineered data-heavy analytical dashboards using Next.js and Recharts, optimizing UI rendering to handle large, complex datasets without performance degradation.</li>
              <li>Built and launched an MVP browser extension (Blujin) for recruiters to automatically extract, filter, and process LinkedIn candidate profiles.</li>
              <li>Developed a generalized POS (Point of Sale) system for restaurant operations from scratch, shipping a fully functional product to production testing in under one month.</li>
              <li>Managed frontend deployment pipelines and release cycles, ensuring stable and reliable application rollouts.</li>
            </ul>
          </div>

          {/* ElPixala */}
          <div className="relative">
            <span className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-zinc-950 border-2 border-cyan-500 shadow-sm shadow-cyan-500/50"></span>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-base font-bold text-white font-display">
                Frontend Developer
              </h4>
              <span className="text-[10px] font-semibold text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                May 2024 – Apr 2025
              </span>
            </div>

            <span className="text-xs font-medium text-zinc-400 block mt-1">
              ElPixala
            </span>

            <ul className="list-disc list-outside ml-4 mt-3 text-xs text-zinc-400 space-y-1.5 font-sans leading-relaxed">
              <li>Developed Acchawalacollege frontend.</li>
              <li>Engineered pixel-perfect, production-ready React components from Figma wireframes utilizing Next.js and Tailwind CSS, significantly accelerating the design-to-code lifecycle.</li>
              <li>Architected seamless data fetching and state management by integrating Strapi headless CMS APIs via TanStack Query, ensuring highly responsive user interfaces.</li>
              <li>Designed and implemented dynamic, nested API filtering mechanisms to handle complex data querying and streamline backend-to-frontend communication.</li>
              <li>Developed robust parsing algorithms to transform rich-text editor data into custom, accessible semantic HTML, precise and clean content rendering.</li>
              <li>Optimized Core Web Vitals and SEO performance by executing advanced front-end techniques, including lazy loading, code splitting, and semantic HTML structuring.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Education Timeline */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-indigo-400 shadow-md">
            <MdOutlineSchool className="text-lg" />
          </div>
          <h3 className="text-xl font-semibold font-display text-white">Education</h3>
        </div>

        <div className="relative pl-8 border-l border-zinc-800/80 ml-5 space-y-6">
          {/* B.Tech */}
          <div className="relative">
            <span className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-zinc-950 border-2 border-violet-500"></span>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-bold text-white font-display">
                B.Tech in Computer Science
              </h4>
              <span className="text-[10px] font-semibold text-violet-400 bg-violet-500/10 border border-violet-500/20 px-2.5 py-1 rounded-full">
                2020 – 2024
              </span>
            </div>
            <span className="text-xs text-zinc-400 block mt-1">
              UIET, Maharshi Dayanand University, Rohtak
            </span>
          </div>

          {/* Senior Secondary */}
          <div className="relative">
            <span className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-zinc-950 border-2 border-indigo-500"></span>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-bold text-white font-display">
                Senior Secondary (12th Grade)
              </h4>
              <span className="text-[10px] font-semibold text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-1 rounded-full">
                Completed 2020
              </span>
            </div>
            <span className="text-xs text-zinc-400 block mt-1">
              RPS Public School, Rewari
            </span>
          </div>
        </div>
      </section>

      {/* Mobile Resume Buttons */}
      <div className="sm:hidden flex flex-col gap-3 mb-10">
        <a
          href="https://docs.google.com/document/d/1FuhTAMiHH_nTtiA28yzb6SbtU5xev_jkRabrtUWtvaU/edit?tab=t.0"
          className="flex items-center justify-center gap-2 text-xs font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 py-3 rounded-xl shadow-lg"
          target="_blank"
          rel="noreferrer"
        >
          <IoDocumentTextOutline className="text-sm" />
          Download CV
        </a>
      </div>

      {/* Skills Section */}
      <section>
        <h3 className="text-xl font-semibold font-display text-white mb-4">My Skills</h3>
        <div className="glass-panel p-6 rounded-2xl">
          <SkillsGrid />
        </div>
      </section>
    </article>
  );
}
